import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Microscope, Flower2, Activity, Droplets, Sparkles, ShieldCheck,
  HeartPulse, Leaf, Stethoscope, MessageCircle, CalendarHeart, ChevronRight, Info,
  Dna, Search, Target,
} from "lucide-react";
import PerlaCard from "../components/PerlaCard";
import { ColposcopiaInfoDialog } from "../components/ColposcopiaInfoDialog";
import WhatsAppCTABlock from "../components/WhatsAppCTABlock";
import { SITE, waLink } from "../lib/site";

const sections = [
  {
    id: "colposcopia",
    chip: "Prevención",
    icon: Microscope,
    title: "Colposcopia y Papanicolaou",
    quote: "La detección oportuna salva vidas.",
    description:
      "Estudios clave para identificar de manera temprana cambios en el cuello uterino. La prevención es la herramienta más poderosa para tu salud.",
    perlas: [
      { icon: ShieldCheck, text: "El Papanicolaou permite detectar cambios tempranos." },
      { icon: Microscope, text: "La colposcopia ayuda a evaluar lesiones cervicales." },
      { icon: Sparkles, text: "La prevención es la mejor herramienta." },
    ],
  },
  {
    id: "vph-pcr",
    chip: "Detección molecular",
    icon: Dna,
    title: "Prueba de PCR para detección de VPH",
    quote: "Un paso adelante en la prevención del cáncer cervicouterino.",
    description:
      "La prueba de PCR (reacción en cadena de la polimerasa) es un estudio molecular de alta precisión que identifica la presencia del ADN del Virus del Papiloma Humano (VPH), incluyendo los serotipos de alto riesgo asociados al cáncer cervicouterino. Complementa al Papanicolaou al detectar la infección incluso antes de que aparezcan cambios celulares.",
    perlas: [
      { icon: Target, text: "Identifica los serotipos de alto riesgo del VPH (16, 18 y otros)." },
      { icon: Search, text: "Detecta la infección antes de que existan lesiones visibles." },
      { icon: ShieldCheck, text: "Mayor sensibilidad y valor preventivo que el Papanicolaou por sí solo." },
    ],
  },
  {
    id: "menopausia",
    chip: "Bienestar",
    icon: Flower2,
    title: "Menopausia y climaterio",
    quote: "Cada mujer vive esta etapa de manera diferente.",
    description:
      "Un acompañamiento médico individualizado para vivir esta etapa con tranquilidad, mejorando síntomas y calidad de vida.",
    perlas: [
      { icon: Leaf, text: "La menopausia es una etapa natural." },
      { icon: HeartPulse, text: "El tratamiento adecuado mejora la calidad de vida." },
      { icon: Sparkles, text: "Cada mujer vive esta etapa de manera diferente." },
    ],
  },
  {
    id: "somp",
    chip: "Alteraciones hormonales",
    icon: Activity,
    title: "Síndrome de Ovario Poli Endocrino Metabólico (antes SOP)",
    quote: "El tratamiento debe ser personalizado.",
    description:
      "Diagnóstico cuidadoso y plan terapéutico que combina seguimiento clínico, hábitos y, cuando es necesario, manejo hormonal.",
    perlas: [
      { icon: ShieldCheck, text: "El diagnóstico temprano ayuda al control hormonal." },
      { icon: Leaf, text: "La alimentación y el estilo de vida son importantes." },
      { icon: Stethoscope, text: "El tratamiento debe ser personalizado." },
    ],
  },
  {
    id: "sangrado",
    chip: "Diagnóstico",
    icon: Droplets,
    title: "Sangrado uterino anormal",
    quote: "Los cambios en el sangrado no deben normalizarse.",
    description:
      "Evaluamos a fondo cualquier cambio en el sangrado menstrual para identificar la causa y ofrecerte un tratamiento efectivo.",
    perlas: [
      { icon: HeartPulse, text: "Los cambios en el sangrado no deben normalizarse." },
      { icon: ShieldCheck, text: "El diagnóstico oportuno evita complicaciones." },
      { icon: Sparkles, text: "Existen tratamientos efectivos." },
    ],
  },
];

export default function SaludGinecologica() {
  return (
    <div data-testid="page-salud-ginecologica">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-brand-snow to-brand-cloud/50" />
        <div className="absolute top-10 right-10 w-96 h-96 rounded-full bg-brand-bright/10 blur-3xl" />
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-brand-medical mb-4">Salud Ginecológica</p>
            <h1 className="font-serif text-5xl md:text-6xl text-brand-deep leading-[1.05]">
              Prevención, diagnóstico y cuidado para <em className="not-italic text-gradient-blue">cada etapa</em>.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-brand-deep/70 font-light leading-relaxed">
              Conoce las principales áreas que abordamos en consulta. Información clara,
              tratamiento personalizado y una experiencia humana.
            </p>
          </motion.div>

          {/* Index pills */}
          <div className="mt-10 flex flex-wrap gap-2.5">
            {sections.map((s) => {
              const pillContent = (
                <>
                  <s.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {s.title.split(" ")[0]}
                  <ChevronRight className="w-3 h-3 opacity-50" />
                </>
              );
              const pillCls = "inline-flex items-center gap-2 rounded-full glass hairline px-4 py-2 text-sm text-brand-deep hover:bg-brand-medical hover:text-white transition-colors cursor-pointer";
              if (s.id === "colposcopia") {
                return (
                  <ColposcopiaInfoDialog
                    key={s.id}
                    trigger={
                      <button type="button" data-testid={`pill-${s.id}`} className={pillCls}>
                        {pillContent}
                      </button>
                    }
                  />
                );
              }
              return (
                <a key={s.id} href={`#${s.id}`} data-testid={`pill-${s.id}`} className={pillCls}>
                  {pillContent}
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTIONS */}
      <div className="mx-auto max-w-7xl px-6 md:px-12 space-y-24 md:space-y-32 pb-20">
        {sections.map((s, idx) => (
          <section key={s.id} id={s.id} className="scroll-mt-28" data-testid={`section-${s.id}`}>
            <div className={`grid lg:grid-cols-12 gap-10 lg:gap-14 items-start ${idx % 2 === 1 ? "lg:flex-row-reverse" : ""}`}>
              <motion.div
                initial={{ opacity: 0, x: idx % 2 === 0 ? -24 : 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7 }}
                className={`lg:col-span-5 ${idx % 2 === 1 ? "lg:col-start-8" : ""}`}
              >
                <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-brand-medical mb-5">
                  <s.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {s.chip}
                </div>
                <h2 className="font-serif text-4xl md:text-5xl text-brand-deep leading-[1.05] mb-5">
                  {s.title}
                </h2>
                <p className="font-serif italic text-xl text-gradient-blue mb-5">
                  “{s.quote}”
                </p>
                <p className="text-brand-deep/70 font-light leading-relaxed text-lg">
                  {s.description}
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  {s.id === "colposcopia" && (
                    <ColposcopiaInfoDialog
                      trigger={
                        <button
                          type="button"
                          data-testid="colposcopia-info-btn"
                          className="inline-flex items-center gap-2 rounded-full bg-brand-deep hover:bg-brand-medical text-white px-6 py-3 font-medium tracking-wide transition-all premium-shadow"
                        >
                          <Info className="w-4 h-4" /> ¿Para qué sirve?
                        </button>
                      }
                    />
                  )}
                  <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer" data-testid={`${s.id}-agendar`} className="inline-flex items-center gap-2 rounded-full bg-brand-medical hover:bg-brand-deep text-white px-6 py-3 font-medium tracking-wide transition-all premium-shadow">
                    <CalendarHeart className="w-4 h-4" /> Agendar consulta
                  </a>
                  <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer" data-testid={`${s.id}-whatsapp`} className="inline-flex items-center gap-2 rounded-full ring-1 ring-brand-medical/40 text-brand-medical hover:bg-white px-6 py-3 font-medium tracking-wide transition-all">
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </a>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className={`lg:col-span-7 ${idx % 2 === 1 ? "lg:col-start-1 lg:row-start-1" : ""}`}
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  {s.perlas.map((p, i) => (
                    <PerlaCard key={i} icon={p.icon} text={p.text} index={i} testid={`${s.id}-perla-${i}`} title />
                  ))}
                  <div className="sm:col-span-2 rounded-3xl p-7 bg-gradient-to-br from-brand-deep to-brand-medical text-white hairline overflow-hidden relative">
                    <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
                    <div className="relative flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-white/15 ring-1 ring-white/20 flex items-center justify-center">
                        <s.icon className="w-6 h-6" strokeWidth={1.4} />
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-brand-silver mb-1">Estética médica elegante</p>
                        <p className="font-serif text-2xl leading-tight">
                          Cuidado clínico moderno con un trato humano cercano.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>
        ))}
      </div>

      {/* WhatsApp CTAs */}
      <WhatsAppCTABlock highlight="cta-valoracion" />
    </div>
  );
}
